import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import './SettingsPage.css';
import { AVAILABLE_THEMES } from '../theme/appTheme';
import { useAuth } from '../context/AuthContext';
import { deleteCurrentUser } from '../services/userService';

const THEME_LABELS = {
  light: 'Light',
  dark: 'Dark',
  synthwave: 'Synthwave',
};

const DELETE_CONFIRMATION_TEXT = 'delete my account';

export default function SettingsPage({ theme, onThemeChange }) {
  const { accessToken, logout } = useAuth();
  const muiTheme = useTheme();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteError, setDeleteError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const closeDeleteDialog = ({ force = false } = {}) => {
    if (isDeleting && !force) {
      return;
    }

    setIsDeleteDialogOpen(false);
    setDeleteConfirmation('');
    setDeleteError(null);
  };

  const openDeleteDialog = () => {
    setDeleteError(null);
    setDeleteConfirmation('');
    setIsDeleteDialogOpen(true);
  };

  const isDeleteConfirmationValid = deleteConfirmation.trim() === DELETE_CONFIRMATION_TEXT;

  const handleDeleteAccount = async () => {
    if (!accessToken) {
      await logout({ shouldCallApi: false });
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await deleteCurrentUser(accessToken);
      closeDeleteDialog({ force: true });
      await logout({ shouldCallApi: false });
    } catch (err) {
      if (err?.status === 401) {
        await logout({ shouldCallApi: false });
        return;
      }

      setDeleteError(err.message || 'Failed to delete account.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box className="settings-page">
      <Paper className="settings-card" elevation={1}>
        <Typography variant="h5" component="h1" gutterBottom>
          Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose your app theme.
        </Typography>

        <Stack spacing={3}>
          <FormControl fullWidth>
            <InputLabel id="theme-select-label">Theme</InputLabel>
            <Select
              labelId="theme-select-label"
              value={theme}
              label="Theme"
              onChange={(event) => onThemeChange(event.target.value)}
            >
              {AVAILABLE_THEMES.map((themeOption) => (
                <MenuItem key={themeOption} value={themeOption}>
                  {THEME_LABELS[themeOption] || themeOption}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider />

          <Box
            className="settings-danger-zone"
            sx={{
              borderColor: alpha(muiTheme.palette.error.main, 0.3),
              background: `linear-gradient(
                135deg,
                ${alpha(muiTheme.palette.error.main, 0.14)} 0%,
                ${alpha(muiTheme.palette.background.paper, 0.96)} 100%
              )`,
              boxShadow: `inset 0 1px 0 ${alpha(muiTheme.palette.common.white, 0.08)}`,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Permanently delete your account and all of your data.
            </Typography>
            <Button
              color="error"
              variant="contained"
              onClick={openDeleteDialog}
              sx={{
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: 'none',
                },
              }}
            >
              Delete Account
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={closeDeleteDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            color: 'text.primary',
            backgroundColor: 'background.paper',
            backgroundImage: `linear-gradient(180deg, ${alpha(
              muiTheme.palette.error.main,
              0.12
            )} 0%, ${alpha(muiTheme.palette.background.paper, 0.98)} 22%)`,
            border: `1px solid ${alpha(muiTheme.palette.error.main, 0.28)}`,
            boxShadow: muiTheme.shadows[12],
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>Delete account?</DialogTitle>
        <DialogContent sx={{ backgroundColor: 'transparent' }}>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <DialogContentText sx={{ color: 'text.secondary' }}>
              This cannot be undone. Deleting your account will permanently remove all of your
              data.
            </DialogContentText>

            {deleteError && <Alert severity="error">{deleteError}</Alert>}

            <TextField
              label="Type 'delete my account' to confirm"
              value={deleteConfirmation}
              onChange={(event) => setDeleteConfirmation(event.target.value)}
              autoComplete="off"
              fullWidth
              disabled={isDeleting}
              helperText="You must type the exact phrase before you can delete your account."
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: alpha(muiTheme.palette.background.default, 0.72),
                },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, backgroundColor: 'transparent' }}>
          <Button onClick={closeDeleteDialog} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteAccount}
            disabled={!isDeleteConfirmationValid || isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
